import type { Category } from '@/graphql/graphql';
import { ContentType } from '@/graphql/graphql';
import { GET_CATEGORIES } from '@/graphql/queries/categories.graphql';
import { apolloClient } from './apollo-client';

export type FetchCategoriesListArgs = {
    contentType: ContentType;
};

type FetchCategoriesListResult = {
    data: Category[];
    total: number;
};

const CATEGORIES_CACHE_TTL_MS = 24 * 3600 * 1000;
const categoriesCache = new Map<
    string,
    {
        value: FetchCategoriesListResult;
        expiresAt: number;
    }
>();
const inFlightCategoriesRequests = new Map<string, Promise<FetchCategoriesListResult>>();

const getLocaleCacheKey = () => {
    if (typeof document === 'undefined') {
        return 'server';
    }

    return (document.documentElement.lang || 'unknown').toLowerCase();
};

const getCategoriesCacheKey = (contentType: ContentType) => `${contentType}:${getLocaleCacheKey()}`;

export async function fetchCategoriesList({ contentType }: FetchCategoriesListArgs) {
    const cacheKey = getCategoriesCacheKey(contentType);
    const now = Date.now();
    const cachedEntry = categoriesCache.get(cacheKey);
    if (cachedEntry && cachedEntry.expiresAt > now) {
        return cachedEntry.value;
    }

    const inFlightRequest = inFlightCategoriesRequests.get(cacheKey);
    if (inFlightRequest) {
        return inFlightRequest;
    }

    const requestPromise: Promise<FetchCategoriesListResult> = apolloClient
        .query({
            query: GET_CATEGORIES,
            variables: { contentType },
        })
        .then((response) => {
            const result: FetchCategoriesListResult = {
                data: response.data.categories,
                total: response.data.categories.length,
            };

            categoriesCache.set(cacheKey, {
                value: result,
                expiresAt: Date.now() + CATEGORIES_CACHE_TTL_MS,
            });

            return result;
        })
        .finally(() => {
            inFlightCategoriesRequests.delete(cacheKey);
        });

    inFlightCategoriesRequests.set(cacheKey, requestPromise);

    return requestPromise;
}
